import type { Rng } from '../rng.js'

import { countedItem, money, NOTES, pickItems, type Item } from './trolley-shop.js'

/**
 * The kinds of problem, easiest first.
 *
 * Which kinds she meets follows her level, the way Monkey's skills unlock —
 * but these are their own skills, kept apart from his. Being fluent at times
 * tables says little about whether a sentence makes sense to her, and folding
 * them together would move her maths level for the wrong reason.
 */
export interface TrolleyQuestion {
  readonly skillKey: string
  /** The shop, so she can see what things cost while she works. */
  readonly prices: readonly Item[]
  /** What she is asked, in words. */
  readonly prompt: string
  /** In cents, always whole, so the number pad needs no decimal point. */
  readonly answer: number
  /** What the answer is, so an answer box can say "45c" rather than "45". */
  readonly answerKind: 'money' | 'count'
}

export interface TrolleySkill {
  readonly key: string
  /** Shown to a parent, not to the player. */
  readonly label: string
  readonly unlocksAt: number
  build(rng: Rng): TrolleyQuestion
}

/** The most change a question will ask her to work out, in cents. */
const MOST_CHANGE = 1000

/** One, two or three of something, which is what a child actually buys. */
function fewOf(rng: Rng): number {
  return 1 + rng.int(3)
}

const totalCost: TrolleySkill = {
  key: 'trolley.total',
  label: 'Adding up a basket',
  unlocksAt: 0,
  build(rng) {
    const prices = pickItems(rng, 3)
    // Non-null: pickItems returns exactly as many as asked while stock lasts,
    // and the stock is far longer than three.
    const first = prices[0]!
    const second = prices[1]!

    const howManyFirst = fewOf(rng)
    const answer = first.cents * howManyFirst + second.cents

    return {
      skillKey: totalCost.key,
      prices,
      prompt: `How much for ${countedItem(first, howManyFirst)} and ${second.one}?`,
      answer,
      answerKind: 'money',
    }
  },
}

const changeFrom: TrolleySkill = {
  key: 'trolley.change',
  label: 'Working out change',
  unlocksAt: 3,
  build(rng) {
    const prices = pickItems(rng, 3)
    const first = prices[0]!
    const second = prices[1]!

    const howManyFirst = fewOf(rng)
    const spent = first.cents * howManyFirst + second.cents

    /*
     * Notes that cover the basket and leave change worth doing in her head.
     *
     * Change from a note that does not cover it is not a harder question, it
     * is a broken one — and capping the *note* was not enough either: an €8.10
     * basket has €10 and €20 as its two smallest, and €11.90 of change is a
     * harder subtraction than this game is about. The difficulty belongs in
     * reading the sentence.
     */
    const enough = NOTES.filter((value) => value > spent && value - spent <= MOST_CHANGE)
    // Guarded rather than trusted to a `??` chain: rng.int(0) throws, so an
    // empty list would crash here before any fallback could run. No stock item
    // can empty it today — the dearest basket is well under the smallest note —
    // but that is a property of the prices, and prices change.
    const note =
      enough.length > 0
        ? enough[rng.int(enough.length)]!
        : (NOTES.find((value) => value > spent) ?? NOTES[NOTES.length - 1]!)

    return {
      skillKey: changeFrom.key,
      prices,
      prompt: `You buy ${countedItem(first, howManyFirst)} and ${second.one}. How much change from ${money(note)}?`,
      answer: note - spent,
      answerKind: 'money',
    }
  },
}

const howMany: TrolleySkill = {
  key: 'trolley.afford',
  label: 'How many can I afford',
  unlocksAt: 5,
  build(rng) {
    const prices = pickItems(rng, 3)
    const item = prices[0]!

    // Chosen so the answer is never zero and rarely more than a handful:
    // "none" is a disheartening answer to work hard for.
    const affordable = 2 + rng.int(4)
    const purse = item.cents * affordable + rng.int(item.cents)

    return {
      skillKey: howMany.key,
      prices,
      prompt: `You have ${money(purse)}. How many ${item.many} can you buy?`,
      answer: affordable,
      answerKind: 'count',
    }
  },
}

const compareBaskets: TrolleySkill = {
  key: 'trolley.compare',
  label: 'Comparing two baskets',
  unlocksAt: 7,
  build(rng) {
    const prices = pickItems(rng, 4)
    const [mine, yours, extra] = [prices[0]!, prices[1]!, prices[2]!]

    let howManyMine = fewOf(rng)
    let mineCost = mine.cents * howManyMine + extra.cents
    const yoursCost = yours.cents * 2

    // Two baskets that cost the same make "how much more" a question whose
    // honest answer is nothing, which is a poor thing to have worked for. One
    // more of the same item is enough to part them, and every item costs
    // something, so this always separates them.
    if (mineCost === yoursCost) {
      howManyMine += 1
      mineCost += mine.cents
    }

    const baskets = `You buy ${countedItem(mine, howManyMine)} and ${extra.one}. Your friend buys ${countedItem(yours, 2)}.`

    /*
     * Who spent more decides how the question is asked.
     *
     * The first version asked "how much more did you spend?" and answered with
     * the absolute difference — so when the friend's basket was dearer, the
     * question asserted something untrue and marked the honest answer wrong.
     * The sums were right and the sentence was a lie, which is the exact
     * failure this game is most likely to have and hardest to notice.
     */
    const iSpentMore = mineCost >= yoursCost

    return {
      skillKey: compareBaskets.key,
      prices,
      prompt: iSpentMore
        ? `${baskets} How much more did you spend?`
        : `${baskets} How much more did your friend spend?`,
      answer: Math.abs(mineCost - yoursCost),
      answerKind: 'money',
    }
  },
}

export const TROLLEY_SKILLS: readonly TrolleySkill[] = [
  totalCost,
  changeFrom,
  howMany,
  compareBaskets,
]

export function trolleySkillByKey(key: string): TrolleySkill | undefined {
  return TROLLEY_SKILLS.find((skill) => skill.key === key)
}
